// Pure helper for CompleteStopModal's wh_event insert chain — extracted so
// the (mappingType, stopType) walk via routeTargetStateFor + advance() can be
// unit-tested without mounting the modal.

import { advance, routeTargetStateFor } from '@onward-delivery/core';
import { RETURN_PICKUP, buildTrackingEvents } from '@/utilities/trackingEvents';
import { SKIP_COMINGLED_STOPS } from '@/constants/featureFlags';

/**
 * Build the `insertEvents` array for an UPDATE_STOP_EVENTS_MANY mutation when
 * a route driver marks a stop complete. For each `{type, order}` pair in the
 * stopOrderMap, walk the canonical lifecycle from `order.event_state` to the
 * helper-derived target state. Orders whose `(mappingType, stopType)`
 * combination has no target state (e.g. invalid combinations) are skipped.
 *
 * Returns a flat array of wh_event objects ready to append to the mutation
 * variables. Pure — no side effects, no Apollo, no React.
 *
 * @param {object} args
 * @param {Array<{type: string, order: object}>} args.stopOrderMap -
 *   the orders touched by this stop and their mapping types
 * @param {string} args.stopType - the stop's type (e.g. 'PICKUP' or 'DROPOFF')
 * @returns {object[]} wh_events in chain order
 */
export const buildStopCompletionEvents = ({ stopOrderMap, stopType }) => {
    const insertEvents = [];
    stopOrderMap.forEach(({ type: mappingType, order }) => {
        const targetState = routeTargetStateFor(mappingType, stopType);
        if (targetState) {
            insertEvents.push(
                ...advance(order, order.event_state, targetState).map((event) => {
                    const { state, transition, __typename, driver, wh_driver, ...rest } = event;
                    return {
                        order_id: order.order_id,
                        ...rest,
                    };
                })
            );
        }
    });
    return insertEvents;
};

// Mirrors onward-app/utils/skipCommingledStops.js. These shippers complete their stops in their own
// app, so any Onward-side lifecycle write for them is fictional. Needs `order_shipper { user { circles } }`.
const orderIsExternal = (order) => !!order?.order_shipper?.user?.circles?.[SKIP_COMINGLED_STOPS];

/**
 * Which item event a stop completion represents, from where the driver physically is. `mappingType`
 * names the *leg*, not the stop, so the two have to be read together:
 *
 *   stop     mapping           where the driver is        event
 *   -------  ----------------  -------------------------  --------------------------------
 *   PICKUP   FULL / PICKUP     customer origin            ITEM_PICKED_UP_FROM_ORIGIN
 *   PICKUP   DROPOFF (leg 2)   warehouse, loading out     ITEM_LOADED_FROM_WAREHOUSE
 *   DROPOFF  PICKUP (leg 1)    warehouse, unloading       ITEM_RECEIVED_INTO_WAREHOUSE
 *   DROPOFF  FULL / DROPOFF    consignee                  ITEM_DELIVERED
 *
 * The crossdock legs are mirror images: leg 1 ends at the warehouse, leg 2 begins there. Recording a
 * leg-2 departure as a receive would leave the item reading as on-hand while it is on a truck.
 *
 * Unrecognised pairs return null and emit nothing.
 */
const itemEventFor = (stopType, mappingType) => {
    if (stopType === 'PICKUP') {
        if (mappingType === 'DROPOFF') {
            return { eventType: 'ITEM_LOADED_FROM_WAREHOUSE' };
        }
        if (mappingType === 'FULL' || mappingType === 'PICKUP') {
            return { eventType: 'ITEM_PICKED_UP_FROM_ORIGIN', returnEventType: RETURN_PICKUP };
        }
        return null;
    }

    if (stopType === 'DROPOFF') {
        if (mappingType === 'PICKUP') {
            return { eventType: 'ITEM_RECEIVED_INTO_WAREHOUSE' };
        }
        if (mappingType === 'FULL' || mappingType === 'DROPOFF') {
            return { eventType: 'ITEM_DELIVERED', returnEventType: RETURN_PICKUP };
        }
        return null;
    }

    return null;
};

/**
 * Build the `trackingEvents` array for an UPDATE_STOP_EVENTS_MANY mutation — the per-item counterpart
 * to buildStopCompletionEvents' wh_event chain. One shared `event_time` so the stop reads as one
 * moment. Skips external orders and orders with no items; no scan gate, unlike mobile.
 *
 * @param {object} args
 * @param {Array<{type: string, order: object}>} args.stopOrderMap - orders on this stop + leg types
 * @param {string} args.stopType - 'PICKUP' or 'DROPOFF'
 * @returns {object[]} tracking_events rows, one per item
 */
export const buildStopCompletionTrackingEvents = ({ stopOrderMap, stopType } = {}) => {
    const eventTime = new Date().toISOString();

    return (stopOrderMap || []).flatMap(({ type: mappingType, order }) => {
        const itemEvent = itemEventFor(stopType, mappingType);
        if (!itemEvent || orderIsExternal(order)) {
            return [];
        }

        return buildTrackingEvents({ items: order?.itemsByOrderId || [], ...itemEvent, eventTime });
    });
};
